import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface AuthState {
  token: string | null;
  username: string | null;
  role: string;
  mustChange: boolean;
  setAuth: (token: string, username: string, role?: string, mustChange?: boolean) => void;
  setToken: (token: string | null) => void;
  clearMustChange: () => void;
  logout: () => void;
  encerrarSessao: () => Promise<boolean>;
}

/**
 * Sessão do operador.
 *
 * T5.3 — o token NÃO é persistido. Antes, o `persist` gravava o estado inteiro
 * em localStorage, incluindo o JWT: qualquer script que rodasse na página (uma
 * dependência comprometida, uma extensão, um XSS) lia a credencial completa, e
 * ela continuava lá depois de fechar o navegador. Um token em disco é uma
 * senha em disco.
 *
 * Agora só a IDENTIDADE fica persistida (`username`, `role`, `mustChange`) —
 * dado de conforto para a UI pintar o cabeçalho e decidir a rota antes da
 * primeira resposta do servidor, e nada disso abre a API sozinho. O access
 * token vive em memória, e some ao recarregar a página.
 *
 * O que segura a sessão através de um F5 é o refresh token, que o backend
 * planta num cookie HttpOnly (`/auth/login`): o JavaScript não o alcança, e
 * `services/api.ts` troca-o por um access novo no boot e em qualquer 401.
 *
 * L2.1 — há DOIS jeitos de a sessão acabar, e eles não são o mesmo ato:
 *
 * - `logout()` só ESQUECE a sessão neste aparelho. É o que `api.ts` chama
 *   quando o servidor já respondeu 401: o token ali já morreu, e pedir ao
 *   backend para revogá-lo seria bater numa porta aberta.
 * - `encerrarSessao()` é o botão "Sair". Ele fala com o servidor ANTES de
 *   esquecer, porque é o servidor que mata o token — `POST /auth/logout` põe o
 *   `jti` do access no denylist do Redis e mata a família do refresh. Sem essa
 *   chamada, "Sair" apenas apagava a tela: o access de 15 minutos continuava
 *   válido para qualquer cópia dele (log de proxy, um computador
 *   compartilhado), que foi exatamente o achado da due diligence.
 */
export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      username: null,
      role: 'editor',
      mustChange: false,
      setAuth: (token, username, role = 'editor', mustChange = false) =>
        set({ token, username, role, mustChange }),
      setToken: (token) => set({ token }),
      clearMustChange: () => set({ mustChange: false }),
      logout: () => set({ token: null, username: null, role: 'editor', mustChange: false }),
      encerrarSessao: async () => {
        // ORDEM: primeiro o servidor, depois a tela. A chamada precisa sair
        // COM o Authorization do token que se quer revogar — se limpássemos o
        // estado antes, `apiFetch` mandaria a requisição sem credencial e o
        // backend não saberia qual `jti` negar. É por isso que o `set` mora no
        // `finally`, e não na primeira linha.
        //
        // Import dinâmico: `services/api.ts` importa ESTE módulo, e a volta
        // estática fecharia um ciclo (mesmo motivo do `useAlertas` lá).
        let revogado = false;
        try {
          const { api } = await import('@/services/api');
          await api.post('/auth/logout');
          revogado = true;
        } catch {
          // Rede caiu, ou o Redis está fora e o backend respondeu 503 (ele se
          // recusa a mentir "ok"). Não prendemos o operador na tela — mas
          // também não fingimos que a sessão morreu: o aviso existe para que
          // ele saiba que o token ainda pode estar de pé até expirar sozinho.
          const { alertar } = await import('@/store/useAlertas');
          alertar(
            'aviso',
            'Sessão encerrada só neste aparelho',
            'O servidor não confirmou a revogação. O acesso anterior pode continuar válido por alguns minutos — se este computador não é seu, troque a senha.',
          );
        } finally {
          set({ token: null, username: null, role: 'editor', mustChange: false });
        }
        return revogado;
      },
    }),
    {
      name: 'wiredesk-auth',
      // A LISTA é o que garante o T5.3. Sem `partialize`, o zustand grava o
      // estado todo, e um campo novo qualquer (um segundo token, amanhã)
      // entraria no localStorage por omissão — que é como este furo nasceu.
      partialize: (s) => ({ username: s.username, role: s.role, mustChange: s.mustChange }),
    },
  ),
);
